import React, {FC, PropsWithChildren, useLayoutEffect, useState} from 'react';
import {IM20ImageBlock, IM20ImageBlockCard} from '@montana/models/generated/cms.interface';
import {Media, MontanaMediaRatios} from '@montana/shared/media';
import {epiMediaRatioToEnum} from '@montana/helpers/epi-mapping.helper';
import {MobileAndTabletPortrait, TabletAndLarger} from '@montana/shared/responsive';
import {Container} from '@montana/shared/container';
import {Link} from '@montana/shared/link';
import {RichText} from '@montana/shared/rich-text';
import cx from 'clsx';

export interface IM20ImageBlockExtended extends IM20ImageBlock {
    withoutContainer?: boolean;
}

export const M20ImagePatternBlock: FC<IM20ImageBlockExtended> = (props: IM20ImageBlockExtended) => {
    const {
        cardTextAlignment,
        backgroundColorHex,
        imageAlignment,
        imageDisplayRatio,
        cards,
        imageSlotsPerRowDesktop,
        imageSlotsPerRowMobile,
        imagesPerRowDesktop,
        imagesPerRowMobile,
        title,
        link,
        showAsCarouselOnMobile = false,
        withoutContainer
    } = props;
    const [noImageCardStyle, setNoImageCardStyle] = useState({})
    const [rowsAreValid] = useState(!!imagesPerRowDesktop && !!imageSlotsPerRowDesktop && !!imagesPerRowMobile && !!imageSlotsPerRowMobile);

    useLayoutEffect(() => {
        const imageRatio = epiMediaRatioToEnum(props.imageDisplayRatio) || MontanaMediaRatios.Square;
        if (typeof imageRatio === 'number') {
            setNoImageCardStyle({paddingTop: `${(imageRatio * 10000) / 100}%`});
        }
    }, []);

    const getWrapperStyles = (mobile?: boolean) => {
        if (mobile && showAsCarouselOnMobile) {
            return ({width: '100%'});
        }

        let width = 100;

        if (rowsAreValid) {
            if (mobile) {
                width = Number(imagesPerRowMobile) / Number(imageSlotsPerRowMobile) * 100;
            } else {
                width = Number(imagesPerRowDesktop) / Number(imageSlotsPerRowDesktop) * 100;
            }
        }

        return ({width: `${width}%`});
    }

    const getImagesPerRow = (mobile?: boolean) => {
        if (rowsAreValid) {
            return mobile ? imagesPerRowMobile : imagesPerRowDesktop;
        } else {
            return 3;
        }
    }



    const renderCards = (mobile: boolean) => (
        <div
            className={cx(
                `image-pattern__cards image-pattern__cards--${getImagesPerRow(mobile)} image-pattern__cards--${imageAlignment}`,
                Boolean(cardTextAlignment) ? `card-text-aligment--${cardTextAlignment}` : 'card-text-aligment--center',
                {'image-pattern__cards--mobile-carousel': mobile && showAsCarouselOnMobile}
            )}
        >
            {cards?.map((card: IM20ImageBlockCard, index: number) => (
                <CardWrapper {...card} key={`card-key__${index}`}>
                    {card.isTextOnly && card.text && <RichText text={card.text}/>}
                    {!card.isTextOnly && (
                        <>
                            {!!card.image ? (
                                <>
                                    <Media
                                        image={card.image}
                                        mediaRatio={imageDisplayRatio ? epiMediaRatioToEnum(imageDisplayRatio) : MontanaMediaRatios.Square}
                                        sizes="360px"
                                    />
                                    {card.tagLine && <div className="image-pattern__tagline">{card.tagLine}</div>}
                                    {(card.title || card.teaser) && (
                                        <div className="image-pattern__card-text-wrapper">
                                            {card.title && <div className="image-pattern__title">{card.title}</div>}
                                            {!!card.teaser &&
                                                <div className="image-pattern__teaser">{card.teaser}</div>}
                                        </div>)}
                                </>
                            ) : (
                                <div className="image-pattern__text-no-image"
                                     style={!card.image ? noImageCardStyle : {}}
                                >
                                    <div className="image-pattern__text-no-image-wrapper">
                                        <div className="image-pattern__title">{card.title}</div>
                                        <div className="image-pattern__teaser">{card.teaser}</div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardWrapper>
            ))}
        </div>
    );

    const generateWrapper = (mobile: boolean) => (
        <div
            className={"image-pattern__wrapper"}
            style={getWrapperStyles(mobile)}
        >
            <div className="image-pattern__header">
                {title && <RichText text={title}/>}
                {link?.url && <Link to={link.url} title={link.title} className="image-pattern__header-link" withStroke>
                    {link.name}
                </Link>}
            </div>
            {renderCards(mobile)}
        </div>
    );

    return (
        <section
            className={`image-pattern image-pattern--${imageAlignment}${!!backgroundColorHex ? ' image-pattern--has-background' : ''}`}
            style={{backgroundColor: backgroundColorHex ? backgroundColorHex : 'inherit'}}
        >
            {withoutContainer
                ? <div className="image-pattern__content">
                    <MobileAndTabletPortrait>
                        {generateWrapper(true)}
                    </MobileAndTabletPortrait>
                    <TabletAndLarger>
                        {generateWrapper(false)}
                    </TabletAndLarger>
                </div>
                : <Container className="image-pattern__content">
                    <MobileAndTabletPortrait>
                        {generateWrapper(true)}
                    </MobileAndTabletPortrait>
                    <TabletAndLarger>
                        {generateWrapper(false)}
                    </TabletAndLarger>
                </Container>}
        </section>
    )
};

const CardWrapper: FC<PropsWithChildren<IM20ImageBlockCard>> = ({children, ...card}) => {
    if (card.link?.url) {
        return (
            <Link
                to={card.link?.url}
                target={card.link?.target}
                title={card.link?.title}
                className={`image-pattern__card${!card.image ? ' image-pattern__card--no-image' : ''}`}
                style={card.backgroundColorHex ? {backgroundColor: card.backgroundColorHex} : undefined}
            >
                {children}
            </Link>
        )
    } else {
        return (
            <div
                className={`image-pattern__card${!card.image ? ' image-pattern__card--no-image' : ''}`}
                style={card.backgroundColorHex ? {backgroundColor: card.backgroundColorHex} : undefined}
            >
                {children}
            </div>
        )
    }
}
